import { PRODUCT_TYPE, ROUTES, USER_ROLES } from '../../common/constants';
import { humanizeEnum } from '../../common/utils';

export const ULTRA_PLAN_ALLOWED_ROLES = [USER_ROLES?.SUPER_ADMIN?.key];

export const INITIAL_ROADMAP_WEEKS = 4;

export const ROADMAPS_TABLE_OFFSET = 241;

const ROADMAPS_LIST_ROUTE = `${ROUTES?.ULTRA_PLAN}${ROUTES?.ROADMAPS}`;
export const ULTRA_PLAN_ROUTES = {
  LIST: ROADMAPS_LIST_ROUTE,
  ROADMAP: `${ROADMAPS_LIST_ROUTE}/:userId/:userSubscriptionId`,
  SUBSCRIBER: `${ROADMAPS_LIST_ROUTE}/:userId/:userSubscriptionId/subscriber`,
};

export const ROADMAP_SLOTS = [
  {
    key: 'BIG_TOY',
    color: 'blue',
    label: 'Big Toy',
    position: 1,
    toyType: PRODUCT_TYPE?.BIG_TOY?.value,
  },
  {
    key: 'HERO_TOY',
    color: 'magenta',
    label: 'Hero Toy',
    position: 2,
    toyType: PRODUCT_TYPE?.TOY?.value,
  },
  {
    key: 'BOOK',
    color: 'gold',
    label: 'Book',
    position: 3,
    toyType: PRODUCT_TYPE?.BOOK?.value,
  },
];

export const ROADMAP_FILE_FILTER = {
  ATTACHED: { label: 'Attached', value: 'ATTACHED' },
  NOT_ATTACHED: { label: 'Not attached', value: 'NOT_ATTACHED' },
};

export const ROADMAP_ERROR_CODES = {
  DEFAULT_CHILD_REQUIRED: 'ULTRA_PLAN_DEFAULT_CHILD_REQUIRED',
};

export const ROADMAP_ERROR_MESSAGES = {
  ULTRA_PLAN_INSUFFICIENT_CANDIDATES:
    "Not enough eligible toys in this child's library and age group to build a week.",
};

export const ULTRA_PLAN_ORDERS_ENABLED = true;

export const DELIVERY_DATE_WEEK = 1;

export const ORDER_DELIVERY_DATE_FORMAT = 'YYYY-MM-DD';

export const SWAP_SUGGESTION_STATUS = { PENDING: 'PENDING' };
export const SWAP_SUGGESTION_DECISION = { APPROVE: 'APPROVE' };
export const SWAP_SUGGESTIONS_LIMIT = 50;

export const GENERATE_MODES = {
  AUTO: 'AUTO',
  PREFERENCES: 'PREFERENCES',
};

export const BROAD_EXPLORATION_CODE = 'broad_exploration';

export const ROADMAP_QUESTIONS = [
  {
    key: 'enjoys',
    getTitle: (name) => `What does ${name} enjoy doing most?`,
    isMultiple: true,
    options: [
      {
        code: 'build_and_connect',
        label: 'Building towers and connecting pieces',
      },
      { code: 'puzzles_and_problems', label: 'Solving puzzles and challenges' },
      {
        code: 'stories_and_words',
        label: 'Listening to stories and looking at books',
      },
      {
        code: 'pretend_and_imagine',
        label: 'Pretending, role-playing, and making up stories',
      },
      {
        code: 'sensory_discovery',
        label: 'Exploring different textures and sensations',
      },
      { code: 'play_together', label: 'Playing games with family and friends' },
      { code: 'make_art', label: 'Drawing, creating, and making things' },
      { code: 'joyful_fun', label: 'Playing simply for fun and enjoyment' },
      {
        code: 'explore_science',
        label: 'Exploring science and discovering how things work',
      },
    ],
  },
  {
    key: 'develop',
    getTitle: (name) => `What would you most like ${name} to develop?`,
    isMultiple: true,
    options: [
      { code: 'think_and_solve', label: 'Thinking and problem-solving' },
      { code: 'connect_with_others', label: 'Social skills and cooperation' },
      {
        code: 'language_and_communication',
        label: 'Language and communication',
      },
      {
        code: 'movement_and_coordination',
        label: 'Fine and gross motor coordination',
      },
      {
        code: 'creativity_and_imagination',
        label: 'Creativity and imagination',
      },
      {
        code: 'moral_and_ethical',
        label: 'Moral values and ethical understanding',
      },
    ],
  },
  {
    key: 'playStyle',
    getTitle: (name) => `What kind of play does ${name} prefer?`,
    isMultiple: true,
    options: [
      { code: 'sensory_discovery', label: 'Independent exploration' },
      { code: 'build_and_connect', label: 'Building and experimenting' },
      { code: 'think_and_solve', label: 'Games that involve thinking' },
      { code: 'pretend_and_imagine', label: 'Pretend play and imagination' },
      { code: 'make_art', label: 'Art and creative activities' },
      {
        code: 'connect_with_others',
        label: 'Playing with parents or other children',
      },
      {
        code: 'language_and_communication',
        label: 'Books, stories, and words',
      },
      {
        code: BROAD_EXPLORATION_CODE,
        label: 'A variety of different activities',
      },
    ],
  },
  {
    key: 'emphasis',
    getTitle: () => 'What would you like the roadmap to emphasize?',
    isMultiple: false,
    options: [
      { code: 'explore_science', label: 'Learning and discovery' },
      {
        code: 'think_and_solve',
        label: 'Logical thinking and problem-solving',
      },
      {
        code: 'creativity_and_imagination',
        label: 'Creativity and imagination',
      },
      {
        code: 'language_and_communication',
        label: 'Language and communication',
      },
      { code: 'movement_and_coordination', label: 'Movement and coordination' },
      { code: 'connect_with_others', label: 'Social and family bonding' },
      {
        code: BROAD_EXPLORATION_CODE,
        label: 'A balanced variety of activities',
      },
    ],
  },
  {
    key: 'extras',
    getTitle: (name) => `Anything else ${name} would enjoy?`,
    isMultiple: true,
    options: [
      { code: 'play_together', label: 'Group games and shared activities' },
      { code: 'make_art', label: 'Art and making things' },
      { code: 'explore_science', label: 'Exploring science' },
      { code: 'sensory_discovery', label: 'Sensory discovery' },
      { code: 'joyful_fun', label: 'Fun-focused activities' },
      { code: 'stories_and_words', label: 'Reading and stories' },
    ],
  },
];

export const SUBSCRIPTION_STATUS_FILTER_OPTIONS = [
  'ACTIVE',
  'EXPIRED',
  'CANCELLED',
  'FAILED',
  'COMPLETED',
  'PENDING',
  'CREATED',
  'AUTHENTICATED',
  'SUCCESS',
]?.map((value) => ({ label: humanizeEnum(value), value }));

export const SUBSCRIBERS_SORT_FIELDS = ['startedAt', 'expireAt'];
